import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import ProjectFormModal from '../components/ProjectFormModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './ProjectList.css';
import { toast } from '../components/Toast';

function ProjectList() {
  const navigate = useNavigate();
  const { user, hasMenuAccess } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE' && !hasMenuAccess('manage-projects');
  const canManage = ['COMPANY_ADMIN', 'BRANCH_MANAGER'].includes(user?.role) || hasMenuAccess('manage-projects');
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedProjects = React.useMemo(() => {
    let sortable = [...projects];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'customerName') {
          aVal = a.customerName || '';
          bVal = b.customerName || '';
        } else if (sortConfig.key === 'startDate') {
          aVal = new Date(a.startDate).getTime();
          bVal = new Date(b.startDate).getTime();
        } else if (sortConfig.key === 'endDate') {
          aVal = a.endDate ? new Date(a.endDate).getTime() : 0;
          bVal = b.endDate ? new Date(b.endDate).getTime() : 0;
        }

        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return sortConfig.direction === 'asc'
            ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
            : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
        }
      });
    }
    return sortable;
  }, [projects, sortConfig]);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
    branchId: '',
    projectTypeId: '',
    page: 0,
    size: 20
  });
  
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0
  });

  useEffect(() => {
    fetchProjects();
    if (!isEmployee) {
      fetchCustomers();
      fetchBranches();
    }
    fetchProjectTypes();
  }, [filters]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page);
      params.append('size', filters.size);
      if (filters.status) params.append('status', filters.status);
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.projectTypeId) params.append('projectTypeId', filters.projectTypeId);
      
      const response = await axios.get(`/projects?${params.toString()}`);
      const data = response.data.data || response.data;
      setProjects(data.content || []);
      setPagination({
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        currentPage: data.page || 0
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers?size=1000');
      const data = response.data.data?.content || response.data.content || [];
      setCustomers(data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await axios.get('/project-types/active');
      const data = response.data.data || response.data;
      setProjectTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch project types:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 0 // Reset to first page on filter change
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      customerId: '',
      branchId: '',
      projectTypeId: '',
      page: 0,
      size: 20
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleCreateProject = () => {
    setSelectedProject(null);
    setShowModal(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    try {
      await axios.delete(`/projects/${projectToDelete.id}`);
      setShowConfirmModal(false);
      setProjectToDelete(null);
      showSuccessMessage('Project deleted successfully');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete project');
      setShowConfirmModal(false);
      setProjectToDelete(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedProject(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedProject(null);
    showSuccessMessage(selectedProject ? 'Project updated successfully' : 'Project created successfully');
    fetchProjects();
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      PLANNED: 'badge-planned',
      ACTIVE: 'badge-active',
      DELAYED: 'badge-delayed',
      COMPLETED: 'badge-completed',
      CANCELLED: 'badge-cancelled'
    };
    return statusClasses[status] || '';
  };

  return (
    <div className="project-list-container">
      <div className="page-header">
        <h1>Projects</h1>
        {canManage && (
          <button className="btn btn-primary" onClick={handleCreateProject}>
            + Create Project
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="proj-filter-row">
          <label className="proj-filter-label">Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="">All Statuses</option>
            <option value="PLANNED">Planned</option>
            <option value="ACTIVE">Active</option>
            <option value="DELAYED">Delayed</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {!isEmployee && (
          <>
            <div className="proj-filter-row" style={{ position: 'relative' }}>
              <label className="proj-filter-label">Customer</label>
              <input
                type="text"
                placeholder="Search Customer..."
                value={customerDropdownOpen ? customerSearch : (customers.find(c => String(c.id) === filters.customerId)?.name || 'All Customers')}
                onFocus={() => {
                  setCustomerDropdownOpen(true);
                  setCustomerSearch('');
                }}
                onBlur={() => setTimeout(() => setCustomerDropdownOpen(false), 250)}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              {customerDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 100,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div
                    onMouseDown={() => {
                      setFilters(prev => ({ ...prev, customerId: '', page: 0 }));
                      setCustomerDropdownOpen(false);
                      setCustomerSearch('');
                    }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', background: '#f9f9f9', fontWeight: 'bold' }}
                  >
                    All Customers
                  </div>
                  {customers
                    .filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                    .map(c => (
                      <div
                        key={c.id}
                        onMouseDown={() => {
                          setFilters(prev => ({ ...prev, customerId: String(c.id), page: 0 }));
                          setCustomerDropdownOpen(false);
                          setCustomerSearch('');
                        }}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                      >
                        {c.name}
                      </div>
                    ))}
                  {customers.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>No customers found</div>
                  )}
                </div>
              )}
            </div>

            <div className="proj-filter-row">
              <label className="proj-filter-label">Branch</label>
              <select name="branchId" value={filters.branchId} onChange={handleFilterChange}>
                <option value="">All Branches</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="proj-filter-row">
          <label className="proj-filter-label">Project Type</label>
          <select name="projectTypeId" value={filters.projectTypeId} onChange={handleFilterChange}>
            <option value="">All Types</option>
            {projectTypes.map(pt => (
              <option key={pt.id} value={pt.id}>{pt.name}</option>
            ))}
          </select>
        </div>

        <button className="btn btn-secondary proj-clear-btn" onClick={handleClearFilters}>
          Clear Filters
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="no-data">No projects found</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name {getSortIcon('name')}</th>
                <th onClick={() => requestSort('branchName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Branch {getSortIcon('branchName')}</th>
                <th onClick={() => requestSort('customerName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Customer {getSortIcon('customerName')}</th>
                <th onClick={() => requestSort('projectTypeName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Type {getSortIcon('projectTypeName')}</th>
                <th onClick={() => requestSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status {getSortIcon('status')}</th>
                <th onClick={() => requestSort('startDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Start Date {getSortIcon('startDate')}</th>
                <th onClick={() => requestSort('endDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>End Date {getSortIcon('endDate')}</th>
                <th onClick={() => requestSort('budget')} style={{ cursor: 'pointer', userSelect: 'none' }}>Budget {getSortIcon('budget')}</th>
                <th onClick={() => requestSort('projectManagerName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Assignee {getSortIcon('projectManagerName')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProjects.map((project) => (
                <tr key={project.id}>
                  <td>{project.name}</td>
                  <td>{project.branchName || '-'}</td>
                  <td>{project.customerName}</td>
                  <td>{project.projectTypeName || '-'}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(project.status)}`}>
                      {project.status}
                    </span>
                  </td>
                  <td>{new Date(project.startDate).toLocaleDateString()}</td>
                  <td>{project.endDate ? new Date(project.endDate).toLocaleDateString() : '-'}</td>
                  <td>{formatCurrency(project.budget)}</td>
                  <td>{project.projectManagerName || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        View
                      </button>
                      {canManage && (
                        <>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditProject(project)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteProject(project)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.currentPage + 1} of {pagination.totalPages}
                {' '}({pagination.totalElements} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= pagination.totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <ProjectFormModal
          project={selectedProject}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Project"
        message={<>Are you sure you want to delete project <strong>{projectToDelete?.name}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setProjectToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default ProjectList;
